import React from "react";

import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Container } from "react-bootstrap";

import ContactFormSuccess from "./ContactFormSuccess.component";
import ContactFormFooter from "./elements/ContactFormFooter.component";
import ContactFormAffordable from "./forms/ContactFormAffordable.component";
import ContactFormBuilders from "./forms/ContactFormBuilders.component";
import ContactFormHomeowners from "./forms/ContactFormHomeowners.component";
import ContactFormInvestors from "./forms/ContactFormInvestors.component";
import useContactContext from "../../hooks/useContactContext";
import i18n from "../../i18n";

import type { ContactCategory } from "@/context/ContactProvider.context";

const RenderForm = (category: ContactCategory): React.JSX.Element | null => {
  switch (category) {
    case "affordable":
      return <ContactFormAffordable />;
    case "builders":
      return <ContactFormBuilders />;
    case "homeowners":
      return <ContactFormHomeowners />;
    case "investors":
      return <ContactFormInvestors />;
    default:
      return null;
  }
};

const getTitle = (category: ContactCategory) => {
  const { t } = i18n;

  switch (category) {
    case "affordable":
      return t("Affordable Housing Providers");
    case "builders":
      return t("Builders & Contractors");
    case "homeowners":
      return t("Homeowners / End Clients");
    case "investors":
      return t("Investors");
    default:
      return "";
  }
};

const RenderHeader = (
  category: ContactCategory,
  setCategory: () => void,
): React.JSX.Element | null => {
  const { t } = i18n;
  return (
    <div
      className="d-flex justify-content-start align-items-center text-start w-100 mb-5 gap-3"
      onClick={() => setCategory()}
      role="button"
    >
      <FontAwesomeIcon icon={faArrowLeft} size="lg" />
      <h3 className="m-0">{`${t("Contact Us")}: ${getTitle(category)}`}</h3>
    </div>
  );
};

const ContactForms = (): React.JSX.Element | null => {
  const { t } = i18n;
  const { category, setCategory, methods, submitted } = useContactContext();

  if (!category) {
    return null;
  }

  if (submitted) {
    return <ContactFormSuccess />;
  }

  const errors = Object.entries(methods.formState.errors);

  return (
    <React.Fragment>
      <Container className="d-flex flex-column justify-content-center align-items-center mt-3 mt-lg-5 pb-5">
        {RenderHeader(category, setCategory)}

        {errors.length > 0 && (
          <Alert variant="danger">
            {t(
              "Oops! Some fields need your attention. Please review and correct the highlighted errors.",
            )}
          </Alert>
        )}
        {RenderForm(category)}
      </Container>
      <ContactFormFooter />
    </React.Fragment>
  );
};

export default ContactForms;
